import React, { useState, useEffect } from 'react'
import styled from 'styled'
import { mediaQueries } from 'styled/core'

import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { actionCreators, GettyActionCreators } from '../../../../actions'

import { GettyStore } from '../../../../store'

import { contentPageSchema } from '../../../../schemas/visitorInfo'
import { visitorInfoSchema } from '../../../../schemas/visitorInfo'

import { connect } from 'containers/store'

import { Fieldset } from 'components/ui/fieldset'
import { FOOTER_HEIGHT } from 'components/ui/footer'
import { NAVBAR_HEIGHT } from 'components/ui/primary-navigation'
import { Input } from 'components/ui/input'


import { VisitorInfoPage } from 'generated/mos/org/getty/visitorinfo'
import { Language } from 'generated/mos/i18n'
import { UpdateVisitorInfoPageRequest } from 'generated/mos/org/getty/visitorinfomanagement'


import { pick, areObjectsEqual } from 'helpers/core'
import { Status } from 'helpers/status'
import { space } from 'helpers/style'
import { translateValues, TranslationFormBags, convertEntityToFormValues } from 'helpers/schema'
import { isEnglish, parseLanguage } from 'helpers/i18n'

import { LANGUAGES, LanguageCode } from '../../../../constants'

type ActionProps = Pick<GettyActionCreators, 'gettyUpdateVisitorInfoPageRequest'>

type ConnectedProps = Pick<GettyStore['getty'], 'visitorInfoUpdate'>

type DirectProps = {
  readonly setSubmit: (submit: () => void) => void;
  readonly setModified: (modifed: boolean | undefined) => void;
  readonly page: VisitorInfoPage.Entity | undefined;
  readonly setHasSubmitted: (submitted: boolean) => void;
}

type Props = ActionProps & ConnectedProps & DirectProps

type FormValues = {
  listTitle: string | undefined;
}

type FormProps = {
  language: Language.Entity;
  formBag: FormBag<FormValues> | undefined;
  setFormBag: (bag: FormBag<FormValues>) => void;
  update: () => void;
}

const HEADER_HEIGHT = 100

const Scroll = styled.div`
  display: flex;
  padding-top: ${space(6)};
  padding-bottom: ${space(15)};
  overflow-x: scroll;
  height: calc(100vh - ${(NAVBAR_HEIGHT + FOOTER_HEIGHT + HEADER_HEIGHT)}px);
`
const Panels = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  padding-left: ${space(9)};
  padding-right: ${space(9)};


  ${mediaQueries.lap} {
    padding-left: ${space(18)};
    padding-right: ${space(18)};
  }

  @media only screen and (min-width: 1600px) {
    padding-left: calc(${space(18)} + (100% - 1600px) / 2);
    padding-right: calc(${space(18)} + (100% - 1600px) / 2);
  }
`

const Panel = styled.div`
  width: calc(100vw - 36px);
  flex: 0 0 auto;
  &:not(:last-child) {
    padding-right: ${space(6)};
    margin-right: ${space(6)};
    border-right: 1px solid ${({ theme }) => theme.color.grayL60};
  }

  ${mediaQueries.hand} {
    width: 500px;
  }
`

const TranslationForm = (props: FormProps) => {
  const { formBag, language, setFormBag, update } = props
  const isDisabled = isEnglish(language)
  return formBag ? (
    <Panel>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
          update()
        }}
        validate={() => ({})}
        >
        <Fieldset legend={parseLanguage(language)}>
          <Input
            label="Title"
            field="headerTitle"
            isDisabled={isDisabled}
            />
          <Input
            label="List title"
            field="listTitle"
            description="The list title is the page's name in the navigation menu."
            isDisabled={isDisabled}
          />
          <Input
            label="Image Alternate Text"
            description="Description of what the image contains if it cannot be shown."
            field="headerImageAltText"
            isDisabled={isDisabled}
          />
          <Input
            component="textarea"
            label="Description"
            field="descriptionText"
            isDisabled={isDisabled}
          />
        </Fieldset>

      </FormData>
    </Panel>
  ) : null
}

const ContentPageTranslationsView = (props: Props) => {
  const {
    gettyUpdateVisitorInfoPageRequest,
    page,
    visitorInfoUpdate,
    setModified,
    setSubmit,
    setHasSubmitted,
  } = props;

  // set up form values
  const [ initialValues, setInitialValues ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined)
  const [ formBags, setFormBags ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined)

  useEffect(() => {
    const getInitialValues = () => {
      const initialValues = LANGUAGES.reduce(
        (acc, curr) => {
          const pageValues: Partial<VisitorInfoPage.AmenityCategory.Entity & VisitorInfoPage.Entity> = pick({
            ...VisitorInfoPage.AmenityCategory.defaults,
            ...VisitorInfoPage.defaults,
            ...page,
            ...(page && page.pageProperties) ? page.pageProperties: null
          }, 'headerTitle', 'headerImageAltText', 'descriptionText', 'listTitle')
          const initialFormValues: FormValues | undefined = page ?
            convertEntityToFormValues<VisitorInfoPage.AmenityCategory.Entity & VisitorInfoPage.Entity | VisitorInfoPage.AmenityCategory.Entity>(pageValues, contentPageSchema, undefined, curr) as FormValues :
            undefined
          return {
            ...acc,
            [curr.languageCode]: createFormBag(initialFormValues)
          }
        // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
        }, {} as TranslationFormBags<FormValues, LanguageCode>)
      return initialValues
    }
    if (page && visitorInfoUpdate.status === Status.Ready) {
      const initValues = getInitialValues()
      setInitialValues(initValues)
      setFormBags(initValues)
    }

  }, [page, visitorInfoUpdate])

  const modified = initialValues && formBags && !areObjectsEqual(initialValues, formBags)

  setModified(modified)

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}
    return errors
  }

  const onSubmit = () => {
    return formBags && page && submit(formBags, page)
  }

  const validate = (formBags: TranslationFormBags<FormValues, LanguageCode>): boolean => {
    let valid = true
    LANGUAGES.forEach((value) => {
      const languageCode: LanguageCode = value.languageCode as LanguageCode
      const bag = validateFormBag(formBags[languageCode], validateForm)
      valid = bag.valid
      updateFormBags(languageCode as LanguageCode)(bag)
    })
    return valid
  }

  const submit = (formBags: TranslationFormBags<FormValues, LanguageCode>, page: VisitorInfoPage.Entity) => {
    // Validate all bags
    const valid = validate(formBags)

    // Check if any bags a invalid
    if(!valid) return false

    setHasSubmitted(true)

    // Assemble Payload
    const translatedValues = translateValues<VisitorInfoPage.Entity, FormValues, LanguageCode>(visitorInfoSchema, page, LANGUAGES, formBags)
    const pageProperties = page && page.pageProperties as VisitorInfoPage.AmenityCategory.Entity
    const translatedPagePropertyValues = translateValues<VisitorInfoPage.AmenityCategory.Entity, FormValues, LanguageCode>(contentPageSchema, pageProperties, LANGUAGES, formBags)

    // Update Page with changes.
    gettyUpdateVisitorInfoPageRequest({
      ...UpdateVisitorInfoPageRequest.defaults,
      visitorInfoPageRef: VisitorInfoPage.mustRef(page.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: ['listTitle', 'amenityCategory']
      },
      listTitle: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: translatedValues('listTitle')
      },
      listIconRef: page.listIconRef,
      pageProperties: {
        ...pageProperties,
        headerTitle: {
          type: "mos.translatable.TranslatableStringValue",
          translatedValues: translatedPagePropertyValues('headerTitle')
        },
        headerImageAltText: {
          type: "mos.translatable.TranslatableStringValue",
          translatedValues: translatedPagePropertyValues('headerImageAltText')
        },
        descriptionText: {
          type: "mos.translatable.TranslatableStringValue",
          translatedValues: translatedPagePropertyValues('descriptionText')
        },
      }
    })

    return false
  }

  const updateFormBags= (languageCode: LanguageCode) =>  (bag: FormBag<FormValues>) => {
    if(formBags) {
      setFormBags({
        ...formBags,
        [languageCode]: bag
      })
    }
  }

  return (
    <Scroll>
      <Panels>
        {formBags && LANGUAGES.map((value) => {
          const languageCode: LanguageCode = value.languageCode as LanguageCode
          return (
            <TranslationForm
              key={languageCode}
              language={value}
              formBag={formBags[languageCode]}
              setFormBag={updateFormBags(languageCode)}
              update={() => {
                setSubmit(() => () => onSubmit())
                setModified(modified)
              }}
            />
          )
        })}
      </Panels>
    </Scroll>
  )
}

export const ContentPageTranslations = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'visitorInfoUpdate'),
  {
    ...pick(actionCreators, 'gettyUpdateVisitorInfoPageRequest')
  },
)(ContentPageTranslationsView)
